import { owlexImageUrl, emptyConnectWalletHtml } from "./nftGallery.js";
import { fetchStakeInfos } from "./lib/stakingData.js";
import { showToast } from "./lib/toast.js";
import { formatTxError } from "./lib/txErrors.js";

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(s) {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/** @type {Set<string>} */
let selectedRoostTokenIds = new Set();

let roostLoadSeq = 0;

function sortTokenIds(ids) {
  return [...ids].sort((a, b) => {
    const na = BigInt(a);
    const nb = BigInt(b);
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
  });
}

/** @returns {string[]} */
export function getSelectedRoostTokenIds() {
  return sortTokenIds(selectedRoostTokenIds);
}

export function getSelectedRoostTokenId() {
  const ids = getSelectedRoostTokenIds();
  return ids.length ? ids[0] : null;
}

export function clearRoostSelection() {
  selectedRoostTokenIds = new Set();
  syncRoostSelectionDom();
}

function syncRoostSelectionDom() {
  document.querySelectorAll(".nft-card--roost[data-token-id]").forEach((el) => {
    const id = el.getAttribute("data-token-id");
    const on = id != null && selectedRoostTokenIds.has(id);
    el.classList.toggle("nft-card--selected", on);
    el.setAttribute("aria-pressed", on ? "true" : "false");
  });

  window.dispatchEvent(
    new CustomEvent("owlex:roost-selection", {
      detail: { tokenIds: getSelectedRoostTokenIds() },
    })
  );
}

function emptyRoostHtml() {
  return `<div class="nft-grid__empty nft-grid__empty--brand nft-grid__empty--with-logo">
    <img src="/gallery/logo.png" alt="" width="72" height="72" class="nft-grid__empty-logo" decoding="async" />
    <p class="nft-grid__empty-copy">Nothing in the roost.</p>
  </div>`;
}

/**
 * @param {bigint | null | undefined} unstakeReadyAt
 * @returns {{ kind: "loading" } | { kind: "ready" } | { kind: "countdown"; timeStr: string }}
 */
function unstakeEtaParts(unstakeReadyAt) {
  if (unstakeReadyAt == null) return { kind: "loading" };
  const now = BigInt(Math.floor(Date.now() / 1000));
  if (now >= unstakeReadyAt) return { kind: "ready" };
  const left = Number(unstakeReadyAt - now);
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const timeStr = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return { kind: "countdown", timeStr };
}

function roostStatusHtml(parts) {
  if (parts.kind === "loading") {
    return `<div class="nft-card__roost-status"><span class="nft-card__roost-pill nft-card__roost-pill--muted">…</span></div>`;
  }
  if (parts.kind === "ready") {
    return `<div class="nft-card__roost-status nft-card__roost-status--ready" aria-label="Ready to unstake">
      <span class="nft-card__roost-pill nft-card__roost-pill--ready">Ready to unstake</span>
    </div>`;
  }
  const timeSafe = escapeHtml(parts.timeStr);
  const ariaSafe = escapeAttr(`Unstake in ${parts.timeStr}`);
  return `<div class="nft-card__roost-status nft-card__roost-status--countdown" aria-label="${ariaSafe}">
      <span class="nft-card__roost-pill nft-card__roost-pill--countdown">
        <span class="nft-card__roost-pill__label">Unstake in</span>
        <span class="nft-card__roost-pill__time">${timeSafe}</span>
      </span>
    </div>`;
}

/**
 * @param {import('@wagmi/core').Config} cfg
 * @param {`0x${string}` | null} userAddress
 * @param {`0x${string}`} stakingAddr
 * @param {string[]} tokenIds
 * @param {{ stakeRows?: Awaited<ReturnType<typeof fetchStakeInfos>> }} [options] Precomputed rows from `fetchStakedTokenIds` to skip a second multicall.
 */
export async function syncRoostGallery(cfg, userAddress, stakingAddr, tokenIds, options = {}) {
  const precomputed = options.stakeRows;
  const grid = document.getElementById("roost-grid");
  if (!grid) return;

  const mySeq = ++roostLoadSeq;
  selectedRoostTokenIds = new Set();
  syncRoostSelectionDom();

  if (!userAddress) {
    grid.innerHTML = emptyConnectWalletHtml();
    return;
  }
  if (tokenIds.length === 0) {
    grid.innerHTML = emptyRoostHtml();
    return;
  }

  if (!cfg) {
    grid.innerHTML = `<p class="nft-grid__empty nft-grid__empty--brand">Wallet not ready.</p>`;
    return;
  }

  grid.innerHTML = `<p class="nft-grid__loading" role="status">Loading…</p>`;

  try {
    const rows =
      Array.isArray(precomputed) && precomputed.length === tokenIds.length
        ? precomputed
        : await fetchStakeInfos(cfg, stakingAddr, tokenIds);
    if (mySeq !== roostLoadSeq) return;

    const bust = Date.now();
    const cards = rows
      .map((row) => {
        const etaParts = unstakeEtaParts(row.unstakeReadyAt);
        return `
      <article
        class="nft-card nft-card--selectable nft-card--roost"
        data-token-id="${escapeAttr(row.tokenId)}"
        data-can-unstake="${row.canUnstake ? "true" : "false"}"
        role="button"
        tabindex="0"
        aria-pressed="false"
      >
        <div class="nft-card__frame">
          <img
            class="nft-card__img"
            src="${owlexImageUrl(row.tokenId, bust)}"
            alt="Owlex #${escapeHtml(row.tokenId)} (staked)"
            width="280"
            height="280"
            loading="lazy"
            decoding="async"
          />
        </div>
        <p class="nft-card__id">#${escapeHtml(row.tokenId)}</p>
        ${roostStatusHtml(etaParts)}
      </article>`;
      })
      .join("");

    grid.innerHTML = `<div class="nft-grid__inner" id="roost-grid-inner">${cards}</div>`;

    const inner = grid.querySelector("#roost-grid-inner");
    if (inner) {
      inner.addEventListener("click", (e) => {
        const card = e.target.closest(".nft-card--roost[data-token-id]");
        if (!card) return;
        const id = card.getAttribute("data-token-id");
        if (id == null) return;
        if (selectedRoostTokenIds.has(id)) selectedRoostTokenIds.delete(id);
        else selectedRoostTokenIds.add(id);
        syncRoostSelectionDom();
      });
      inner.addEventListener("keydown", (e) => {
        const card = e.target.closest(".nft-card--roost[data-token-id]");
        if (!card) return;
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        const id = card.getAttribute("data-token-id");
        if (id == null) return;
        if (selectedRoostTokenIds.has(id)) selectedRoostTokenIds.delete(id);
        else selectedRoostTokenIds.add(id);
        syncRoostSelectionDom();
      });
    }

    syncRoostSelectionDom();
  } catch (err) {
    if (mySeq !== roostLoadSeq) return;
    console.error("[owlex-staking] roost load failed", err);
    showToast(formatTxError(err), { variant: "error" });
    grid.innerHTML = `<p class="nft-grid__empty nft-grid__empty--brand">Couldn’t load.</p>`;
  }
}
